import dayjs from 'dayjs';
import { globalValue } from '../apps/AppContext';
import { LineItem, Tax, TaxBreakup, Txn } from '../../types';
import { fetchTax, fetchTaxes } from '../../api';
import { useState, useEffect, Dispatch, SetStateAction } from 'react';
import DOMPurify from 'dompurify';

// Server-rendered txn/payment print templates are full HTML documents (with <style>/<link>/<meta>),
// so they need WHOLE_DOCUMENT mode rather than the default fragment sanitization.
export function sanitizeRenderedHtml(html: string): string {
  return DOMPurify.sanitize(html, {
    WHOLE_DOCUMENT: true,
    ADD_TAGS: ['style', 'link', 'meta'],
    ADD_ATTR: ['target', 'rel', 'href', 'charset'],
    ALLOW_DATA_ATTR: true,
    FORCE_BODY: false,
  });
}

export function getEnumValueFromString<T extends { [key: string]: string }>(enumObj: T, value: string): T[keyof T] | undefined {
  const enumValues = Object.values(enumObj) as string[];
  return enumValues.includes(value) ? (value as T[keyof T]) : undefined;
}

// `dateFormat` values come from the `DateFormats` enum (types.ts), which is persisted as
// part of a business's settings (bizMaster.bizDetails.dateFormat) using date-fns-style
// tokens (e.g. "dd-MM-yyyy") — those stored values can't be changed without a data migration.
// dayjs uses different tokens for year/day-of-month (YYYY/DD vs date-fns's yyyy/dd), so
// translate before formatting. MM/MMMM are identical between the two libraries and pass
// through unchanged; this only covers the token set actually used by DateFormats today
// (yyyy, dd, d, MM, MMMM) — extend it if a format using other tokens (e.g. weekday names) is
// ever added.
function toDayjsFormat(dateFnsFormat: string): string {
  return dateFnsFormat
    .replace(/yyyy/g, 'YYYY')
    .replace(/dd/g, 'DD')
    .replace(/d/g, 'D');
}

export function formatDate(date?: Date, dateFormat: string = 'dd-MM-yyyy'): string {
  if (!date) {
    return ''; // Return an empty string if the date is undefined
  }

  try {
    return dayjs(date).format(toDayjsFormat(dateFormat));
  } catch (error: any) {
    console.error('Date formatting error:', error);
    return ''; // Return an empty string if formatting fails
  }
}

export function displayDate(date?: Date) {
  const bizDateFormat = globalValue.bizMaster.bizDetails.dateFormat
  return formatDate(date, bizDateFormat)
}

// Returns a Date representing 00:00:00.000 on the given calendar date (year/month/day
// as the *caller* intends it — e.g. what the user clicked in a browser-local calendar
// widget, or a plain y/m/d constructed with `new Date(y, m, d)`) anchored to the
// business's configured timezone. This is the single canonical implementation — every
// producer of a report/filter `fromDate`/`toDate` must go through this (or endOfDayInBizTZ)
// so that "anchored to biz-tz midnight" is an invariant that actually holds everywhere,
// rather than something each caller has to remember to do correctly on its own.
export function startOfDayInBizTZ(date: Date): Date {
  const timezone = globalValue?.bizMaster?.bizDetails?.timeZone
    || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const year = date.getFullYear();
  const month = date.getMonth();
  const day = date.getDate();
  const base = new Date(Date.UTC(year, month, day, 0, 0, 0, 0));
  const tzWall = new Date(base.toLocaleString('en-US', { timeZone: timezone }));
  const utcWall = new Date(base.toLocaleString('en-US', { timeZone: 'UTC' }));
  const offset = utcWall.getTime() - tzWall.getTime();
  return new Date(base.getTime() + offset);
}

// 23:59:59.999 biz-local on the same calendar date as startOfDayInBizTZ(date). Pure
// arithmetic on the anchored instant — safe in any timezone once the instant is anchored.
export function endOfDayInBizTZ(date: Date): Date {
  return new Date(startOfDayInBizTZ(date).getTime() + 24 * 60 * 60 * 1000 - 1);
}

// Like displayDate, but for Date values anchored to biz-timezone day boundaries (e.g.
// dateFilter.fromDate/toDate — see getFiscalYearDates / startOfDayInBizTZ above).
// displayDate/format() reads a Date's *browser-local* calendar day, so a biz-tz-anchored
// instant (e.g. Dec 31 23:59:59.999 in the biz timezone) can land on the wrong day — a
// different one — when the browser's timezone differs from the biz's (e.g. shows as Jan 1).
// Re-anchor to the biz-tz calendar day first, mirroring DatePicker.tsx's bizTZToLocalMidnight.
export function displayDateInBizTZ(date?: Date) {
  if (!date) return '';
  const timezone = globalValue?.bizMaster?.bizDetails?.timeZone;
  if (!timezone) return displayDate(date);
  const dateStr = date.toLocaleDateString('en-CA', { timeZone: timezone }); // "YYYY-MM-DD"
  const [year, month, day] = dateStr.split('-').map(Number);
  return displayDate(new Date(year, month - 1, day));
}

// MBAI-28: scroll to the first invalid field by element id or name attribute
export function scrollToFirstError(elementId: string): void {
  const element = document.getElementById(elementId) ?? document.querySelector<HTMLElement>(`[name="${elementId}"]`);
  if (element) {
    element.scrollIntoView({ behavior: 'smooth', block: 'center' });
    element.focus();
  }
}

const createTaxBreakupObject = (
  lineItemTax: Tax,
  taxId: string,
  lineItem: LineItem,
  taxList : Tax[]
) => {

  if (lineItemTax?.subTaxes?.length > 0) {
    const subTax = lineItemTax.subTaxes.find(
      subTax => subTax.taxName === taxId
    );
    
    if (subTax) {
      const compositeTaxId = `${subTax.taxName}_${subTax.taxPct}`;
      const taxInfo = taxList.find(tax=>tax.taxId===subTax.taxName)
      return new TaxBreakup({
        taxId: compositeTaxId,            // ✅ unique per percentage
        taxName: taxInfo?.taxName || subTax.taxName || "Tax",        // optional but recommended
        taxAcct: lineItemTax.taxAcct,
        taxPct: subTax.taxPct,
        taxAmt: (subTax.taxPct / 100) * (lineItem?.amtBeforeTax || 0),
        taxGroupRate: lineItemTax.taxPct,
        itemId: lineItem?.itemId || '',
      });
    }
  } 
  else if (taxId === lineItem.taxId) {
    const compositeTaxId = `${lineItem.taxId}_${lineItem.taxPct}`;
    const taxInfo = taxList.find(tax=>tax.taxId===lineItem.taxId)
    return new TaxBreakup({
      taxId: compositeTaxId,              // ✅ unique per percentage
      taxName: taxInfo?.taxName || "Tax",            // optional
      taxPct: lineItem.taxPct,
      taxAcct: lineItemTax?.taxAcct,
      taxAmt: ((lineItem?.taxPct || 0) / 100) * (lineItem?.amtBeforeTax || 0),
      taxGroupRate: lineItemTax.taxPct,
      itemId: lineItem?.itemId || '',
    });
  }
};


/**
 * MBAI-88: shipping tax lives on txnHeader (shippingtaxId/shippingtaxAmt), not on any line item, so
 * the line-item based extractTaxBreakup never sees it and the GSTR reports dropped it. Returns the
 * shipping charge as its own report row (taxable value + tax split by component), or null when the
 * txn has no shipping tax. Mirrors the backend's isTaxGroup split (subTax.taxName is a taxId ref).
 */
export function getShippingTaxRow(
  txnHeader: { shippingtaxId?: string; shippingtaxAmt?: number; shippingAmt?: number } | undefined,
  taxList: Tax[]
) {
  const shippingTaxAmt = Number(txnHeader?.shippingtaxAmt || 0);
  if (shippingTaxAmt <= 0) return null;

  const taxMap = new Map(taxList.map(tax => [tax.taxId, tax]));
  const shippingTax = taxMap.get(txnHeader?.shippingtaxId || '');
  const row = { taxRate: 0, taxableValue: Number(txnHeader?.shippingAmt || 0), integratedTax: 0, centralTax: 0, stateUtTax: 0, cess: 0 };

  const allocate = (taxName: string, amt: number) => {
    const name = (taxName || '').toUpperCase();
    if (name.startsWith('IGST')) row.integratedTax += amt;
    else if (name.startsWith('CGST')) row.centralTax += amt;
    else if (name.startsWith('SGST')) row.stateUtTax += amt;
    else if (name.startsWith('CESS')) row.cess += amt;
    else if (name.includes('GST')) { // plain "GST": split evenly, as the print tax summary does
      row.centralTax += amt / 2;
      row.stateUtTax += amt / 2;
    }
  };

  if (shippingTax && shippingTax.subTaxes?.length > 0) {
    shippingTax.subTaxes.forEach(subTax => {
      row.taxRate += subTax.taxPct || 0;
      allocate(taxMap.get(subTax.taxName)?.taxName || subTax.taxName, (subTax.taxPct / 100) * shippingTaxAmt);
    });
  } else {
    row.taxRate = shippingTax?.taxPct || 0;
    allocate(shippingTax?.taxName || '', shippingTaxAmt);
  }
  return row;
}

export async function processTxnsWithTaxBreakup(
  txns: any[],
) {
  // Iterate through each transaction in the array
  if (!txns|| !Array.isArray(txns) || txns?.length === 0) {
    return [];
  }
  const taxes = await fetchTaxes()
  const txnWithAttributes = [];
  for (const txn of txns) {
    const taxBreakup = await extractTaxBreakup(txn, taxes,true);
    txnWithAttributes.push({
      ...txn,
      taxBreakup, // Add the taxBreakup property
    });
  }
  // Return the updated array of transactions
  return txnWithAttributes;
}

export async function extractTaxBreakup(txn: Txn, taxList: Tax[],byItem:boolean=false): Promise<TaxBreakup[]> {
  const taxMap = new Map(taxList.map(tax => [tax.taxId, tax]));
  const uniqueTaxIds = [
    ...new Set(
      txn?.lineItems?.map(lineItem => lineItem.taxId).filter(id => id != null && id !== '')
    ),
  ];
  
  const aggregateTaxBreakup: Record<string, TaxBreakup> = {};

  // Function to process tax groups and update uniqueTaxIds
  const processTaxGroups = () => {
    const removeList: string[] = []; // List of tax IDs to remove
  
    uniqueTaxIds?.forEach(taxId => {
      const tax = taxMap.get(taxId || '');
  
      if (tax?.subTaxes && tax.subTaxes.length > 0) {
        // Add the current taxId to the remove list
        removeList.push(taxId||'');
  
        // Add subTaxIds from taxGroup.taxCodes to uniqueTaxIds
        tax.subTaxes.forEach(subTax => {
          if (!uniqueTaxIds.includes(subTax.taxName)) {
            uniqueTaxIds.push(subTax.taxName);
          }
        });
      }
    });
  
    // Remove all IDs in removeList from uniqueTaxIds
    removeList.forEach(taxIdToRemove => {
      const index = uniqueTaxIds.indexOf(taxIdToRemove);
      if (index !== -1) {
        uniqueTaxIds.splice(index, 1);
      }
    });
  
  };

  // Process tax groups to update uniqueTaxIds
  processTaxGroups();

  // Function to create tax breakups for a line item
  // Function to create tax breakups for a line item
  const createTaxBreakup = (lineItem: LineItem): TaxBreakup[] => {
    const lineItemTax = taxMap.get(lineItem?.taxId || '');
    // Return an empty array if lineItemTax is undefined
    if (!lineItemTax) {
      return [];
    }
    // Map uniqueTaxIds to create tax breakup objects, handling potential undefined values in create Tax Breakup Object
    return uniqueTaxIds.map(taxId => {
      return taxId ? createTaxBreakupObject(lineItemTax, taxId, lineItem,taxList) : undefined;
    }).filter(taxBreakup => taxBreakup !== undefined) as TaxBreakup[]; // Filter out undefined entries if any
  };

  // Loop through line items to compute tax breakups and aggregate them
  txn?.lineItems?.forEach(lineItem => {
    const taxBreakup = createTaxBreakup(lineItem);

    // Aggregate the tax breakups
    taxBreakup.forEach((tax: TaxBreakup) => {
      if (!tax?.taxId) return;

      const taxName = taxList.find(taxMaster => taxMaster.taxId === tax.taxId)?.taxName || tax.taxId;
      const itemId = (byItem) ?  lineItem?.itemId || '' : undefined
      // Check and initialize if not already defined
      const taxId = tax?.taxId || '';
      if (!aggregateTaxBreakup[taxId+itemId]) {
        aggregateTaxBreakup[taxId+itemId] = {
          taxId: taxId,
          taxName: taxList.find(taxMaster => taxMaster.taxId === taxId)?.taxName || tax.taxName || taxId,
          taxPct: tax.taxPct || 0,
          taxGroupRate:tax.taxGroupRate||0,
          itemId:itemId,
          taxAmt: 0, // Initialize taxAmt to 0
        };
      }

      // Now, safely update taxAmt
      if (aggregateTaxBreakup[taxId+itemId]) aggregateTaxBreakup[taxId+itemId].taxAmt += tax?.taxAmt || 0;
      
    });
  });

  // Apply invoice-level discount: scale all tax amounts by the discount ratio
  const { subtotalAmt, discountAmt, discountPct, isDiscountPct } = txn.txnHeader;
  if (subtotalAmt && subtotalAmt > 0) {
    const invoiceDiscount = isDiscountPct
      ? (subtotalAmt * (discountPct ?? 0)) / 100
      : (discountAmt ?? 0);

    if (invoiceDiscount > 0) {
      const discountRatio = (subtotalAmt - invoiceDiscount) / subtotalAmt;
      Object.values(aggregateTaxBreakup).forEach((tax) => {
        tax.taxAmt = parseFloat((tax.taxAmt * discountRatio).toFixed(2));
      });
    }
  }

  // If no line items contributed taxes, fall back to txnHeader.txnTaxes (direct tax entry)
  const result = Object.values(aggregateTaxBreakup);
  if (result.length === 0 && (txn.txnHeader?.txnTaxes?.length ?? 0) > 0) {
    return txn.txnHeader.txnTaxes!.map(t => new TaxBreakup({
      taxId: t.taxId,
      taxName: (t as any).taxName,
      taxPct: t.taxPct,
      taxAmt: t.taxAmt,
    }));
  }

  return result;
}


export function getFiscalYearDates(fiscalYear: any, yearOffset = 0, timezone?: string) {
  const now = new Date();

  // Get current date/time in the biz timezone (falls back to browser local)
  const nowInTz = timezone ? new Date(now.toLocaleString('en-US', { timeZone: timezone })) : now;
  const currYear = nowInTz.getFullYear();
  const currMonth = nowInTz.getMonth();
  const year = currYear + yearOffset;

  // `date` here is a Date freshly constructed from calendar y/m/d (e.g. `new Date(fyYear,
  // startMonth, startDay)`), so its local getters already reflect the intended calendar date.
  // Anchor that calendar date to 00:00:00 in the biz timezone via an offset comparison — not
  // by round-tripping through `date.toLocaleString(..., { timeZone }) -> new Date(string)`,
  // which reparses the (timezone-less) wall-clock string in the *browser's* timezone and
  // silently lands on the wrong day whenever the browser's timezone differs from the biz's
  // (e.g. "This Year" rendering as 12/31-12/30 instead of 1/1-12/31).
  const setStartOfDay = (date: Date): Date => {
    if (!timezone) {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    }
    const base = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0));
    const tzWall = new Date(base.toLocaleString('en-US', { timeZone: timezone }));
    const utcWall = new Date(base.toLocaleString('en-US', { timeZone: 'UTC' }));
    const offset = utcWall.getTime() - tzWall.getTime();
    return new Date(base.getTime() + offset);
  };
  const setEndOfDay = (date: Date): Date => {
    const start = setStartOfDay(date);
    return new Date(start.getTime() + 24 * 60 * 60 * 1000 - 1);
  };

  const startParts = fiscalYear.start.split('/');
  const endParts = fiscalYear.end.split('/');
  const startMonth = parseInt(startParts[1], 10) - 1;
  const startDay = parseInt(startParts[0], 10);
  const endMonth = parseInt(endParts[1], 10) - 1;
  const endDay = parseInt(endParts[0], 10);

  const fyYear = (startMonth > 1 && startMonth > currMonth) ? year - 1 : year;
  const crossesYear = endMonth < startMonth || (endMonth === startMonth && endDay < startDay);

  let fromDate = setStartOfDay(new Date(fyYear, startMonth, startDay));
  let toDate = setEndOfDay(new Date(crossesYear ? fyYear + 1 : fyYear, endMonth, endDay));

  if (fromDate.getTime() > nowInTz.getTime()) {
    fromDate = setStartOfDay(new Date(fyYear - 1, startMonth, startDay));
    toDate = setEndOfDay(new Date(crossesYear ? fyYear : fyYear - 1, endMonth, endDay));
  }

  return { fromDate, toDate };
}


export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, Dispatch<SetStateAction<T>>] {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = localStorage.getItem(key);
      return item !== null ? JSON.parse(item) as T : initialValue;
    } catch (error) {
      console.error('Error reading localStorage key:', key, error);
      return initialValue;
    }
  });

  const setValue: Dispatch<SetStateAction<T>> = (value) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.error('Error setting localStorage key:', key, error);
    }
  };

  return [storedValue, setValue];
}


